/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Form, Grid, Tooltip } from '@mairistem/composants';
import { NationaliteInput } from '@mairistem/administres-utils';

import { Steps } from '@/types';
import { Qualification } from '@/enums';

import { Section, SectionHeader } from '@/components/Common';
import { QualificationInput } from '@/components/Common/Qualification';
import { AssistantContext } from '@/contexts';

const StepInformationsComplementaires = () => {
  const {
    onChange,
    situationScolaire,
    diplome,
    specialite,
    profession,
    identifiantNationaliteBis,
    hasDoubleNationalite,
    scolarise,
    activiteProfessionnelle,
    errors,
  } = React.useContext(AssistantContext);

  const errorsStep = errors?.[Steps.InformationsComplementaires] || {};

  const handleNationaliteCheckboxChange = React.useCallback(() => {
    if (hasDoubleNationalite) {
      onChange(null, {
        name: 'identifiantNationaliteBis',
        value: null,
      });
    }

    onChange(null, {
      name: 'hasDoubleNationalite',
      value: !hasDoubleNationalite,
    });
  }, [hasDoubleNationalite, onChange]);

  const handleSituationChange = React.useCallback(
    (situation: string, value: boolean) => {
      onChange(null, {
        name: situation,
        value,
      });
    },
    [onChange],
  );

  return (
    <Grid as={Form} style={{ width: '100%' }}>
      <Grid.Row>
        <Grid.Column>
          <Section title="INFORMATIONS COMPLÉMENTAIRES">
            <Grid as={Form} padded>
              <Grid.Row style={{ paddingBottom: 0 }}>
                <Grid.Column width={16}>
                  <SectionHeader title="Qualifications" />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row verticalAlign="middle" style={{ padding: '1em' }}>
                <Grid.Column computer={2} tablet={8} mobile={16}>
                  <Form.Field required label="Scolarisé ou en formation ?" />
                </Grid.Column>
                <Grid.Column computer={1} tablet={8} mobile={16}>
                  <Tooltip
                    trigger={
                      <Form.Checkbox
                        radio
                        data-testid="scolarise-checkbox-step-informations-complementaires-oui"
                        name="scolarise"
                        label="Oui"
                        checked={scolarise === true}
                        onChange={() =>
                          handleSituationChange('scolarise', true)
                        }
                      />
                    }
                    type={errorsStep?.scolarise ? 'error' : ''}
                    message={errorsStep?.scolarise}
                  />
                </Grid.Column>
                <Grid.Column computer={1} tablet={8} mobile={16}>
                  <Tooltip
                    trigger={
                      <Form.Checkbox
                        radio
                        data-testid="scolarise-checkbox-step-informations-complementaires-non"
                        name="scolarise"
                        label="Non"
                        checked={scolarise === false}
                        onChange={() =>
                          handleSituationChange('scolarise', false)
                        }
                      />
                    }
                    type={errorsStep?.scolarise ? 'error' : ''}
                    message={errorsStep?.scolarise}
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={8} mobile={16}>
                  <QualificationInput
                    qualification={Qualification.SituationScolaire}
                    onChange={onChange}
                    value={situationScolaire}
                    disabled={scolarise === false}
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={8} mobile={16}>
                  <QualificationInput
                    qualification={Qualification.Specialite}
                    onChange={onChange}
                    value={specialite}
                    disabled={scolarise === false}
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={8} mobile={16}>
                  <QualificationInput
                    qualification={Qualification.Diplome}
                    onChange={onChange}
                    value={diplome}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row verticalAlign="middle" style={{ padding: '1em' }}>
                <Grid.Column computer={2} tablet={8} mobile={16}>
                  <Form.Field required label="Activité professionnelle ?" />
                </Grid.Column>
                <Grid.Column computer={1} tablet={8} mobile={16}>
                  <Tooltip
                    trigger={
                      <Form.Checkbox
                        data-testid="activite-professionnelle-checkbox-step-informations-complementaires-oui"
                        name="activiteProfessionnelle"
                        label="Oui"
                        radio
                        checked={activiteProfessionnelle === true}
                        onChange={() =>
                          handleSituationChange('activiteProfessionnelle', true)
                        }
                      />
                    }
                    type={errorsStep?.activiteProfessionnelle ? 'error' : ''}
                    message={errorsStep?.activiteProfessionnelle}
                  />
                </Grid.Column>
                <Grid.Column computer={1} tablet={8} mobile={16}>
                  <Tooltip
                    trigger={
                      <Form.Checkbox
                        radio
                        data-testid="activite-professionnelle-checkbox-step-informations-complementaires-non"
                        name="activiteProfessionnelle"
                        label="Non"
                        checked={activiteProfessionnelle === false}
                        onChange={() =>
                          handleSituationChange(
                            'activiteProfessionnelle',
                            false,
                          )
                        }
                      />
                    }
                    type={errorsStep?.activiteProfessionnelle ? 'error' : ''}
                    message={errorsStep?.activiteProfessionnelle}
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={8} mobile={16}>
                  <QualificationInput
                    qualification={Qualification.Profession}
                    onChange={onChange}
                    value={profession}
                    disabled={activiteProfessionnelle === false}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row style={{ paddingBottom: 0 }}>
                <Grid.Column width={16}>
                  <SectionHeader title="Binational" />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row verticalAlign="middle" style={{ padding: '1em' }}>
                <Grid.Column computer={4} tablet={8} mobile={16}>
                  <Tooltip
                    trigger={
                      <Form.Checkbox
                        data-testid="double-nationalite-checkbox-step-informations-complementaires"
                        name="doubleNationalite"
                        label="Bénéficie d'une double nationalité - 2ème nationalité"
                        checked={hasDoubleNationalite}
                        onChange={handleNationaliteCheckboxChange}
                      />
                    }
                    type={errorsStep?.identifiantNationaliteBis ? 'error' : ''}
                    message={errorsStep?.identifiantNationaliteBis}
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={8} mobile={16}>
                  <NationaliteInput
                    bis
                    identifiantNationalite={identifiantNationaliteBis}
                    disabled={!hasDoubleNationalite}
                    errors={{
                      identifiantNationalite:
                        errorsStep?.identifiantNationaliteBis,
                    }}
                    onChange={onChange}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Section>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default StepInformationsComplementaires;
